import React from 'react'
import PropTypes from 'prop-types'
import usePagination from './usePagination'
import classNames from 'classnames'
import styles from './Pagination.module.scss'

/**
 * Drop-in pagination component.
 *
 * @param {object} props with currentPage, setCurrentPage, and pageCount
 * @returns {React.Component} a Pagination component
 */
const Pagination = ({ currentPage, setCurrentPage, pageCount, disabled }) => {
  // Generate list of items
  const { items } = usePagination({ currentPage, setCurrentPage, pageCount, disabled })

  return (
    <>
      {/* Only display the pagination component if there's more than one page */}
      {pageCount >= 2 && (
        <div className={styles.container}>
          <ul className="pagination justify-content-center">
            {items.map(({ type, page, selected, disabled, onClick }, index) => {
              return (
                <li
                  className={classNames('page-item', { disabled: disabled, selected: selected })}
                  key={`pagination-${index}`}
                >
                  <PaginationItem
                    type={type}
                    page={page}
                    disabled={disabled}
                    onClick={onClick}
                    key={`pagination-item-${index}`}
                  />
                </li>
              )
            })}
          </ul>
        </div>
      )}
    </>
  )
}

const PaginationItem = ({ type, page, onClick, disabled }) => {
  switch (type) {
    case 'page':
      return (
        <button tabIndex="-1" onClick={onClick} className="page-link" disabled={disabled}>
          {page}
        </button>
      )
    case 'next':
      return (
        <button
          tabIndex="-1"
          onClick={onClick}
          className={classNames(styles.next, disabled ? styles.disabled : null)}
          disabled={disabled}
        >
          Next
        </button>
      )
    case 'previous':
      return (
        <button
          tabIndex="-1"
          onClick={onClick}
          className={classNames(styles.previous, disabled ? styles.disabled : null)}
          disabled={disabled}
        >
          Previous
        </button>
      )
    case 'start-ellipsis':
    case 'end-ellipsis':
      return '...'
    default:
      return (
        <button
          tabIndex="-1"
          onClick={onClick}
          className={classNames('page-link')}
          disabled={disabled}
        >
          {page}
        </button>
      )
  }
}

Pagination.propTypes = {
  currentPage: PropTypes.number.isRequired,
  setCurrentPage: PropTypes.func.isRequired,
  pageCount: PropTypes.number.isRequired
}

export default Pagination
